const isLowStock = (variant) => {
  if (!variant) return false;

  return (
    variant.inventory_management === "shopify" &&
    variant.inventory_policy === "deny" &&
    variant.inventory_quantity <= parseInt(window.theme.low_stock_threshold)
  );
};

class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.hasUserSelectedVariant = false;
    this.userClickedVariant = false;
  }

  connectedCallback() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("discover_other_bags") === "true") {
      const summaryEl = document.querySelector(
        '[data-accordion="discover-other-sizes"] summary',
      );
      if (summaryEl) {
        summaryEl.click();
      }
    }

    const urlVariantId = urlParams.get("variant");
    if (urlVariantId) {
      this.hasUserSelectedVariant = true;
    }

    const data = window.scrapeSectionData(this);
    const allSoldOut = data.variants.every(
      (v) =>
        v.inventory_quantity <= 0 &&
        v.inventory_management === "shopify" &&
        v.inventory_policy === "deny",
    );

    const hasWaitlist = data.optionsByVariant.some(
      (v) =>
        v.inventory_quantity <= 0 &&
        v.inventory_policy === "deny" &&
        v.metafields?.custom?.waitlist === true,
    );

    const idInput = this.querySelector(".product-form__variant-id");
    const initVariantId =
      idInput?.getAttribute("value") || idInput?.dataset.variantid;
    const initVariant = data.variants.find((v) => v.id == initVariantId);

    const comingSoonTag = data.product.tags?.includes("!coming-soon");
    const preorderTag = data.product.tags?.includes("preorder");
    this.isPreorder = preorderTag;

    const initIsWaitlist = initVariant?.metafields?.custom?.waitlist === true;
    const initIsComingSoon =
      comingSoonTag &&
      data.variants.every(
        (v) =>
          v.inventory_quantity <= 0 &&
          v.inventory_management === "shopify" &&
          v.inventory_policy === "deny",
      );

    const effectiveIsWaitlist = initIsWaitlist || hasWaitlist;

    this._updateAtcs(
      initVariant,
      effectiveIsWaitlist,
      initIsComingSoon,
      this.isPreorder,
    );

    this.addEventListener("variant:changed", (e) => {
      const { variant, frameSizeVariants, isWaitlist, isComingSoon } = e.detail;
      // Save on instance for resize
      this.currentVariant = variant;
      this.isWaitlist = isWaitlist;
      this.isComingSoon = isComingSoon;
      this.hasUserSelectedVariant = true;
      this.userClickedVariant = true;
      this._updateAtcs(variant, isWaitlist, isComingSoon, this.isPreorder);
      this._updateMonogramButtons(variant);
      this._updateIdInputs(variant);

      if (!variant) return;

      this._updatePrices(variant);
      this._updateUrl(variant);
      this._updateOptionPrices(frameSizeVariants);
      this._updateSkus(variant);
      this._updateStockMessages(variant);
      this._updateGtmLabel(variant);
    });

    const handleResize = () => {
      if (this.currentVariant) {
        this._updateAtcs(
          this.currentVariant,
          this.isWaitlist,
          this.isComingSoon,
          this.isPreorder,
        );
      }
    };
    window.addEventListener("resize", handleResize);
    this._handleResize = handleResize;
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this._handleResize);
  }

  get atcs() {
    return this.querySelectorAll("[data-product-form-atc]");
  }

  get waitlist() {
    return this.querySelectorAll("[data-product-waitlist]");
  }

  get monogramButtons() {
    return this.querySelectorAll("[data-monogram-button]");
  }

  get prices() {
    return this.querySelectorAll("[data-main-product-price]");
  }

  get idInputs() {
    return this.querySelectorAll('input[name="id"]');
  }

  get monogramTriggers() {
    return this.querySelectorAll("monogram-trigger");
  }

  get skus() {
    return this.closest(".shopify-section").querySelectorAll(
      "[data-description-box-sku]",
    );
  }

  get stockMessages() {
    return this.querySelectorAll("[data-low-stock]");
  }

  get updateUrl() {
    return !this.hasAttribute("disable-update-url");
  }

  _updateGtmLabel(variant) {
    if (!variant) {
      this.dataset.gtmLabel = "";
    }

    if (!variant.available) {
      this.dataset.gtmLabel = "Sold out";
      return;
    }

    if (isLowStock(variant)) {
      this.dataset.gtmLabel = "Low in stock";
    }
  }

  _updateAtcs(variant, isWaitlist, isComingSoon, isPreorder) {
    const isDesktop = window.matchMedia("(min-width:1024px)").matches;
    const data = window.scrapeSectionData(this);
    const productHasSize = data.product?.options?.some(
      (opt) => opt?.trim().toLowerCase() === "size",
    );
    const allSoldOut = data.variants.every((v) => !v.available);

    const waitlistDesktop = this.querySelectorAll(".waitlist-desktop");
    const waitlistMobile = this.querySelectorAll(".waitlist-mobile");
    const atcDesktop = this.querySelectorAll(".product-form__actions-custom");
    let atcMobile;
    if (
      isComingSoon &&
      allSoldOut &&
      data.variants.every((v) => v.inventory_policy === "deny")
    ) {
      atcMobile = this.querySelectorAll(".product-form__button-mobile .product-form__atc");
    } else {
      atcMobile = this.querySelectorAll(".product-form__button-mobile");
    }

    // Hide all buttons first
    [
      ...waitlistDesktop,
      ...waitlistMobile,
      ...atcDesktop,
      ...atcMobile,
    ].forEach((el) => {
      el.style.display = "none";
      el.classList.remove("is-active");
    });

    // 1️⃣ Coming Soon + All Sold Out
    if (
      isComingSoon &&
      allSoldOut &&
      data.variants.every((v) => v.inventory_policy === "deny")
    ) {
      const visibleWaitlists = isDesktop ? waitlistDesktop : waitlistMobile;
      visibleWaitlists.forEach((el) => {
        el.style.display = "block";
        el.classList.add("is-active");
        
        const btn = el.querySelector("button");
        if (btn)
          btn.textContent = window.theme.localeStrings.product.coming_soon;
      });
      return;
    }

    // 2️⃣ Not available & has waitlist → show Add to Waitlist
    if (!variant?.available && isWaitlist) {
      const visibleWaitlists = isDesktop ? waitlistDesktop : waitlistMobile;
      visibleWaitlists.forEach((el) => {
        if (!isDesktop) {
          const parent = el.closest(".product-form__button-mobile");
          if (parent) {
            parent.style.display = "block";
            parent.classList.add("is-active");
            const atcBtn = parent.querySelector(".product-form__atc");
            if (atcBtn) {
              atcBtn.style.display = "none";
              atcBtn.classList.remove("is-active");
            }
          }
        }
        el.style.display = "block";
        el.classList.add("is-active");
        const btn = el.querySelector("button");
        if (btn) {
          btn.textContent = window.theme.localeStrings.product.add_to_waitlist;
          btn.disabled = false;
        }
      });
      return;
    }

    // 3️⃣ Not available & no waitlist → Sold Out
    if (!variant?.available && !isWaitlist) {
      const visibleAtcs = isDesktop ? atcDesktop : atcMobile;

      visibleAtcs.forEach((el) => {
        el.style.display = "block";
        el.classList.add("is-active");
        const btn = el.querySelector("button");
        if (btn) {
          btn.textContent = window.theme.localeStrings.product.sold_out;
          btn.disabled = true;
        }
      });
      return;
    }

    // 4️⃣ Variant available → Add to Cart
    if (variant?.available) {
      const visibleAtcs = isDesktop ? atcDesktop : atcMobile;

      visibleAtcs.forEach((el) => {
        el.style.display = "block";
        el.classList.add("is-active");

        let btn = el.querySelector("button");
        let btnText = btn?.querySelector("add-to-cart");

        if (!isDesktop) {
          const wrapper = el;

          const waitlist = wrapper.querySelector(".waitlist-mobile");
          const atcBtn = wrapper.querySelector(".product-form__atc");
          if (waitlist) {
            waitlist.style.display = "none";
            waitlist.classList.remove("is-active");
          }
          if (atcBtn) {
            atcBtn.style.display = "block";
            atcBtn.classList.add("is-active");
            atcBtn.disabled = false;

            btn = atcBtn;
            btnText = atcBtn.querySelector("add-to-cart");
          }
        }

        if (btn && btnText) {
          btn.disabled = false;

          if (isPreorder && !this.userClickedVariant) {
            btnText.textContent = window.theme.localeStrings.product.preorder;
          } else {
            btnText.textContent =
              productHasSize && !this.hasUserSelectedVariant
                ? "Select a Size"
                : window.theme.localeStrings.product.add_to_cart;
          }
        }
      });
    }
  }

  disableAllIfSoldOut() {
    const allSoldOut = this.data.variants.every((v) => !v.available);
    const isMobile = window.matchMedia("(max-width: 1023px)").matches;
    const detailsContainer = this.querySelector("conditional-details details");

    if (allSoldOut && detailsContainer && isMobile) {
      detailsContainer.removeAttribute("open");

      detailsContainer.addEventListener("toggle", (e) => {
        if (!detailsContainer.hasAttribute("open")) {
          e.preventDefault();
          detailsContainer.removeAttribute("open");
        }
      });

      const summary = detailsContainer.querySelector("summary");
      if (summary) {
        summary.style.pointerEvents = "none";
      }
    }

    if (!isMobile) {
      if (allSoldOut && detailsContainer) {
        this.querySelectorAll(
          "label.product-form__option-label input.product-form__option-value--disabled",
        ).forEach((input) => {
          input.disabled = true;
        });
      }
    }
  }

  _updateMonogramButtons(variant) {
    this.monogramButtons.forEach((button) => {
      button.disabled = !variant || !variant.available;
      const monogramTrigger = button.querySelector("monogram-trigger");
      if (monogramTrigger) {
        monogramTrigger.innerHTML =
          window.theme.localeStrings.product.add_monogram;
        monogramTrigger.setAttribute("current-variant-id", variant.id);
      }
    });
  }

  _updatePrices(variant) {
    this.prices.forEach((price) => {
      this._updatePriceEl(price, variant);
    });
  }

  _updateSkus(variant) {
    this.skus.forEach((sku) => {
      sku.innerHTML = variant.sku || "";
    });
  }

  _updateOptionPrices(frameSizeVariants) {
    frameSizeVariants.forEach(({ productOptionId, variant }) => {
      const productOptionPrice = this.querySelector(
        `[data-option-value-price="${productOptionId}"]`,
      );

      if (!productOptionPrice) return;

      this._updatePriceEl(productOptionPrice, variant);
    });
  }

  _updateIdInputs(variant) {
    this.idInputs.forEach((idInput) => {
      idInput.value = variant?.id || "";
    });
  }

  _updateStockMessages(variant) {
    if (variant.inventory_quantity <= 0) {
      this.stockMessages.forEach((message) => {
        message.style.display = "none";
      });
      return;
    }

    const lowStock = isLowStock(variant);
    this.stockMessages.forEach((message) => {
      message.style.display = lowStock ? "" : "none";
    });
  }

  _updateUrl(variant) {
    const url = new URL(window.location);

    if (!window.Shopify.designMode && this.updateUrl) {
      url.searchParams.set("variant", variant.id);
      window.theme.replaceState("", "", url);
    }
  }

  get _priceHtmlByVariant() {
    if (!this._priceHtmlMap) {
      const data = window.scrapeSectionData(this) || {};
      const entries = (data.optionsByVariant || [])
        .filter((option) => option.priceHtml)
        .map((option) => [String(option.variantId), option.priceHtml]);

      this._priceHtmlMap = new Map(entries);
    }

    return this._priceHtmlMap;
  }

  _updatePriceEl(priceRoot, variant) {
    if (!priceRoot || !variant) return;

    const html = this._priceHtmlByVariant.get(String(variant.id));

    if (html) {
      const holder = document.createElement("div");
      holder.innerHTML = html;
      const rendered = holder.querySelector("price-snippet");

      if (rendered) {
        priceRoot.innerHTML = rendered.innerHTML;
      }
    }

    priceRoot.removeAttribute("dynamic");
    priceRoot.removeAttribute("price");
    priceRoot.removeAttribute("compare-at-price");
    priceRoot.removeAttribute("currency");
  }
}

if (!customElements.get("product-form")) {
  customElements.define("product-form", ProductForm);
}
